import { useMemo, useRef, useEffect, useState } from 'react';
import useAdvancedSearchContext from 'components/consumer/consumer-search/advanced-search-next/hooks/useAdvancedSearchContext';
import useProductVariantsToShow from 'hooks/useProductListingData/useProductVariantsToShow';
import { getColorFilterSignature } from 'components/consumer/consumer-search/advanced-search-next/components/AdvancedSearchProductHits/utils';
import useStaticProductsData from './useStaticProductsData';
import addDerivedProductData from '../useProductListingData/helpers/addDerivedProductData';
const useInfiniteStaticProducts = ({
  productIds: allProductIds,
  searchKey
}) => {
  const fetchedIdsRef = useRef(new Set());
  const previousProductIdsRef = useRef([]);
  const previousProductIdsSignatureRef = useRef('');
  const previousSearchKeyRef = useRef(searchKey);
  const currentColorFilterSignature = useMemo(() => getColorFilterSignature(searchKey), [searchKey]);
  const previousColorFilterSignatureRef = useRef('');
  const {
    selectedColors
  } = useAdvancedSearchContext();
  const [accumulatedProducts, setAccumulatedProducts] = useState([]);
  const [accumulatedUpholsteryOptions, setAccumulatedUpholsteryOptions] = useState([]);
  const upholsteryLabelFilters = useMemo(() => accumulatedUpholsteryOptions.map(option => option.label), [accumulatedUpholsteryOptions]);
  useEffect(() => {
    const colorFilterSignatureUnchanged = currentColorFilterSignature === previousColorFilterSignatureRef.current;
    if (searchKey !== previousSearchKeyRef.current && colorFilterSignatureUnchanged) {
      // When search term/context changes, clear all accumulated data
      // This ensures no products from the previous search appear in the new results
      fetchedIdsRef.current = new Set();
      setAccumulatedProducts([]);
      setAccumulatedUpholsteryOptions([]);
      previousProductIdsRef.current = [];
      previousProductIdsSignatureRef.current = '';
      previousSearchKeyRef.current = searchKey;
    }
    previousColorFilterSignatureRef.current = currentColorFilterSignature;
  }, [searchKey, currentColorFilterSignature]);
  useEffect(() => {
    const currentIds = allProductIds || [];
    const previousIds = previousProductIdsRef.current || [];
    const currentSignature = currentIds.slice(0, 5).join(',');
    const previousSignature = previousProductIdsSignatureRef.current;
    const firstIdChanged = previousIds.length > 0 && currentIds.length > 0 && String(previousIds[0]) !== String(currentIds[0]);
    const signatureChanged = previousSignature !== '' && currentSignature !== '' && previousSignature !== currentSignature && !currentSignature.startsWith(previousSignature);
    const shouldReset = firstIdChanged || signatureChanged;
    const colorFilterSignatureChanged = currentColorFilterSignature !== previousColorFilterSignatureRef.current;

    // If Algolia returned completely different product IDs (e.g., filters changed significantly),
    // reset the accumulation to prevent showing stale products from the previous result set
    if (shouldReset && colorFilterSignatureChanged) {
      fetchedIdsRef.current = new Set();
      setAccumulatedProducts([]);
      setAccumulatedUpholsteryOptions([]);
    }
    previousProductIdsRef.current = currentIds;
    previousProductIdsSignatureRef.current = currentSignature;
    previousColorFilterSignatureRef.current = currentColorFilterSignature;
  }, [allProductIds, searchKey, currentColorFilterSignature]);
  const newProductIds = useMemo(() => {
    if (!allProductIds?.length) return [];
    const fetchedNormalized = Array.from(fetchedIdsRef.current).map(id => String(id));
    return allProductIds.filter(id_0 => !fetchedNormalized.includes(String(id_0)));
  }, [allProductIds]);
  const shouldFetch = newProductIds.length > 0;
  const currentData = useStaticProductsData({
    productIds: newProductIds
  }, {
    skip: !shouldFetch
  });
  const newProductIdsRef = useRef(newProductIds);
  newProductIdsRef.current = newProductIds;
  useEffect(() => {
    const products = currentData?.products;
    const upholsteryOptions = currentData?.upholsteryOptions;
    const isLoading = currentData?.loading;
    const newIds = newProductIdsRef.current;
    if (!products && !isLoading) return;
    if (products?.length > 0) {
      // Only mark IDs as fetched if their data actually came back.
      const returnedIds = new Set(products.map(p => String(p.id)));
      newIds.forEach(id_1 => {
        if (returnedIds.has(String(id_1))) {
          fetchedIdsRef.current.add(String(id_1));
        }
      });
      setAccumulatedProducts(prev => {
        const existingIds = new Set(prev.map(p_0 => String(p_0.id)));
        const newProducts = products.filter(p_1 => !existingIds.has(String(p_1.id)));
        return [...prev, ...newProducts];
      });
      if (upholsteryOptions?.length > 0) {
        setAccumulatedUpholsteryOptions(prev_0 => {
          // only accumulate upholstery option if its not already present
          const existingIds_0 = new Set(prev_0.map(opt => opt.id));
          const newOptions = upholsteryOptions.filter(opt_0 => !existingIds_0.has(opt_0.id));
          return [...prev_0, ...newOptions];
        });
      }
    }
  }, [currentData?.products, currentData?.upholsteryOptions, currentData?.loading]);
  const finalProducts = useMemo(() => {
    if (!allProductIds?.length || !accumulatedProducts?.length) {
      return accumulatedProducts;
    }

    // Only return products that are in the current allProductIds list
    // This ensures no stale products from previous searches appear
    // when filter state changes and Algolia returns a new set of IDs
    const requestedIdsNormalized = new Set(allProductIds.map(id_2 => String(id_2)));
    return accumulatedProducts.filter(product => {
      const productIdNormalized = String(product.id);
      return requestedIdsNormalized.has(productIdNormalized);
    });
  }, [accumulatedProducts, allProductIds]);
  const productsWithAdditionalData = useMemo(() => finalProducts.map(product_0 => addDerivedProductData({
    product: product_0,
    upholsteryOptions: accumulatedUpholsteryOptions
  })), [accumulatedUpholsteryOptions, finalProducts]);
  const colorFiltersArray = useMemo(() => Object.keys(selectedColors).filter(color => selectedColors[color]), [selectedColors]);
  const productsWithVariantsToShow = useProductVariantsToShow({
    colorFilters: colorFiltersArray.length > 0 ? colorFiltersArray : undefined,
    products: productsWithAdditionalData,
    upholsteryLabelFilters
  });
  const allNewIdsAccumulated = useMemo(() => {
    if (!newProductIds.length) return true;
    if (currentData?.loading) return false;
    // Query done. Only wait for IDs that the DB actually returned.
    const returnedProducts = currentData?.products;
    if (!returnedProducts?.length) return true;
    const accumulatedIdsSet = new Set(accumulatedProducts.map(p_2 => String(p_2.id)));
    return returnedProducts.every(p_3 => accumulatedIdsSet.has(String(p_3.id)));
  }, [newProductIds.length, currentData?.loading, currentData?.products, accumulatedProducts]);
  return {
    products: productsWithVariantsToShow ?? [],
    loading: currentData?.loading || !allNewIdsAccumulated
  };
};
export default useInfiniteStaticProducts;